import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';

import { Store } from '@ngrx/store';
import {
  Observable,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
} from 'rxjs';
import { SubSink } from 'subsink';

import { VipDiscountCompanyDto } from '@app/api/marketplace/models';
import { normalizeForSearch } from '@shared/helpers/normalize-for-search.helper';

import {
  VipDiscountsSellingActions,
  VipDiscountsSellingSelectors,
} from '../../store/vip-discounts-selling.slice';

@Component({
  selector: 'app-vip-discounts-given',
  templateUrl: './vip-discounts-given.component.html',
  styleUrls: ['./vip-discounts-given.component.scss'],
})
export class VipDiscountsGivenComponent implements OnInit, OnDestroy {
  subs = new SubSink();
  selectedDiscounts!: Array<string>;

  pageSizeOptions = [25, 125, 1000];
  pageSize = 25;
  pageIndex = 0;

  companyFilterControl = new FormControl<Array<string>>([], {
    nonNullable: true,
  });
  companySearchControl = new FormControl<string>('', { nonNullable: true });
  productNameControl = new FormControl<string>('', { nonNullable: true });

  constructor(private store: Store) {}

  isAllGivenMerchantsSelected$ = this.store.select(
    VipDiscountsSellingSelectors.isAllGivenMerchantsSelected
  );

  discountsGiven$ = this.store.select(
    VipDiscountsSellingSelectors.selectDiscountsGiven
  );

  discountsGivenLoading$ = this.store.select(
    VipDiscountsSellingSelectors.selectDiscountsGivenLoading
  );

  totalElements$ = this.store.select(
    VipDiscountsSellingSelectors.selectGivenTotalElements
  );

  private companies$ = this.store.select(
    VipDiscountsSellingSelectors.selectGivenCompanies
  );

  // Companies matching the in-panel search box (case- and accent-insensitive).
  filteredCompanies$: Observable<Array<VipDiscountCompanyDto>> = combineLatest([
    this.companies$,
    this.companySearchControl.valueChanges.pipe(startWith('')),
  ]).pipe(
    map(([companies, search]) => {
      const needle = normalizeForSearch(search);
      if (!needle) {
        return companies;
      }
      return companies.filter((company) =>
        normalizeForSearch(company.companyName).includes(needle)
      );
    })
  );

  // Currently selected companies, shown as removable chips below the select.
  selectedCompanies$: Observable<Array<VipDiscountCompanyDto>> = combineLatest([
    this.companies$,
    this.companyFilterControl.valueChanges.pipe(
      startWith(this.companyFilterControl.value)
    ),
  ]).pipe(
    map(([companies, selectedIds]) =>
      companies.filter((company) =>
        selectedIds.includes(company.merchantId ?? '')
      )
    )
  );

  ngOnInit(): void {
    this.store.dispatch(
      VipDiscountsSellingActions.loadGivenCompanies.trigger()
    );
    this.loadPage();

    this.subs.add(
      this.companyFilterControl.valueChanges.subscribe(() => {
        this.pageIndex = 0;
        this.loadPage();
      })
    );

    this.subs.add(
      this.productNameControl.valueChanges
        .pipe(debounceTime(400), distinctUntilChanged())
        .subscribe(() => {
          this.pageIndex = 0;
          this.loadPage();
        })
    );

    this.subs.add(
      this.store
        .select(
          VipDiscountsSellingSelectors.selectSelectedGivenProductsForGroupOperation
        )
        .subscribe((discounts) => (this.selectedDiscounts = discounts))
    );
  }

  onPage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadPage();
  }

  removeCompany(merchantId: string): void {
    this.companyFilterControl.setValue(
      this.companyFilterControl.value.filter((id) => id !== merchantId)
    );
  }

  onCompanyPanelToggle(opened: boolean): void {
    if (!opened) {
      this.companySearchControl.setValue('');
    }
  }

  private loadPage(): void {
    this.store.dispatch(
      VipDiscountsSellingActions.loadDiscountsGiven.trigger({
        page: this.pageIndex,
        size: this.pageSize,
        companyIds: this.companyFilterControl.value,
        productName: this.productNameControl.value,
      })
    );
  }

  deleteSelectedItems() {
    this.store.dispatch(
      VipDiscountsSellingActions.deleteSelectedDiscounts.trigger({
        selectedDiscounts: this.selectedDiscounts,
      })
    );
  }

  ngOnDestroy(): void {
    this.store.dispatch(VipDiscountsSellingActions.clearIndexes.trigger());
    this.subs.unsubscribe();
  }
}
